import { Link } from 'react-router-dom';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { contentDetailPath, isProductType, isServiceType, isAnnonceUnavailable, type MarketplaceAnnonce } from '@/lib/marketplaceCatalog';
import VendorRatingBadge from '@/components/feature/marketplace/VendorRatingBadge';
import type { VendorRating } from '@/lib/vendorReviews';

// ─────────────────────────────────────────────────────────────
// Carte d'annonce d'un vendeur (marketplace mixte).
// Affichée en grille sur l'accueil et sur les pages de listes.
// Toute la carte renvoie vers la fiche détaillée ; le bouton
// « Contacter » reste un appel direct (lien téléphone).
// ─────────────────────────────────────────────────────────────

interface AnnonceCardProps {
  annonce: MarketplaceAnnonce;
  monaie: string;
  className?: string;
  /** Nom de la boutique qui a publié le contenu (affiché sous le titre). */
  sellerName?: string;
  /** Note moyenne de la boutique (rend le classement visible). */
  sellerRating?: VendorRating | null;
  /** Lien de réservation (services) — affiche un bouton « Réserver ». */
  reserveHref?: string;
  /** Noms de catégories (id → nom) pour afficher la chaîne complète
   *  Catégorie › Sous-catégorie › Sous-sous-catégorie sur la carte. */
  categoryNames?: Record<number, string>;
}

function priceLabel(prix: string | null, display: (amount: number, from?: string) => string, native: string): string {
  const n = Number(prix);
  if (!prix || Number.isNaN(n) || n <= 0) return 'Prix à discuter';
  return display(n, native);
}

function typeBadge(annonce: MarketplaceAnnonce): { label: string; icon: string; className: string } {
  if (isProductType(annonce.type)) {
    return { label: 'Produit', icon: 'ri-shopping-bag-3-line', className: 'bg-primary-100 text-primary-700' };
  }
  if (isServiceType(annonce.type)) {
    return { label: 'Service', icon: 'ri-service-line', className: 'bg-secondary-100 text-secondary-900' };
  }
  return {
    label: (annonce.type || 'Annonce').trim(),
    icon: 'ri-megaphone-line',
    className: 'bg-accent-100 text-accent-900',
  };
}

export default function AnnonceCard({ annonce, monaie, className = '', sellerName, sellerRating, reserveHref, categoryNames }: AnnonceCardProps) {
  const img = resolveMediaUrl(annonce.image);
  const badge = typeBadge(annonce);
  // Prix convertis dans la devise d'affichage choisie par le visiteur
  // (taux du jour), la monnaie du marchand restant la référence.
  const { display, original } = useDisplayCurrency(monaie);
  const rawPrice = Number(annonce.prix);
  const hasPrice = Boolean(annonce.prix) && !Number.isNaN(rawPrice) && rawPrice > 0;
  const convertedSub = hasPrice ? original(rawPrice, monaie) : null;

  // Chaîne de catégorie complète (du plus général au plus précis).
  const categoryChain = categoryNames
    ? [annonce.idcategorie_grandparent, annonce.idcategorie_parent, annonce.idcategorie]
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && n > 0)
        .map((id) => ({ id, nom: categoryNames[id] }))
        .filter((c) => Boolean(c.nom))
    : [];

  return (
    <div
      className={`relative group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-all duration-200 flex flex-col ${className}`}
    >
      {/* Lien plein-carte : rend toute la surface cliquable (href réel). */}
      <Link
        to={contentDetailPath(annonce)}
        className="absolute inset-0 z-0 rounded-lg"
        aria-label={annonce.titre}
      ></Link>

      <div className="relative h-24 md:h-32 bg-background-100 overflow-hidden pointer-events-none">
        {img ? (
          <img
            src={img}
            alt={annonce.titre}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="ri-image-line text-3xl text-foreground-300"></i>
          </div>
        )}
        <span
          className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full text-[9px] md:text-[10px] font-semibold capitalize flex items-center gap-1 ${badge.className}`}
        >
          <i className={badge.icon}></i>
          {badge.label}
        </span>
        {annonce.is_boost && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent-500 text-background-50 flex items-center gap-1">
            <i className="ri-flashlight-fill"></i>
            À la une
          </span>
        )}
        {isAnnonceUnavailable(annonce) && (
          <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-500 text-white flex items-center gap-1">
            <i className="ri-forbid-2-line"></i>
            Indisponible
          </span>
        )}
      </div>

      <div className="p-2 md:p-3 flex flex-col flex-1 pointer-events-none">
        <h3 className="text-[11px] md:text-sm font-semibold text-foreground-900 mb-1 line-clamp-2 leading-snug">{annonce.titre}</h3>
        {categoryChain.length > 0 && (
          <div className="hidden md:flex items-center gap-1 flex-wrap mb-1.5">
            {categoryChain.map((c, idx) => (
              <span key={c.id} className="flex items-center gap-1">
                {idx > 0 && <i className="ri-arrow-right-s-line text-foreground-300 text-[10px]"></i>}
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-50 text-secondary-700 whitespace-nowrap">{c.nom}</span>
              </span>
            ))}
          </div>
        )}
        {sellerName && (
          <span className="text-[10px] md:text-[11px] text-foreground-500 flex items-center gap-1.5 mb-1 min-w-0">
            <i className="ri-store-2-line text-primary-500 flex-shrink-0"></i>
            <span className="truncate">{sellerName}</span>
            {sellerRating && (sellerRating.average > 0 || sellerRating.count > 0) && (
              <VendorRatingBadge average={sellerRating.average} count={sellerRating.count} className="flex-shrink-0" />
            )}
          </span>
        )}
        {annonce.description && (
          <p className="hidden md:block text-xs text-foreground-500 line-clamp-2 mb-2">{annonce.description}</p>
        )}
        <div className="mt-auto pt-1.5 md:pt-2">
          <div className="text-xs md:text-base font-bold text-primary-600 leading-tight">{priceLabel(annonce.prix, display, monaie)}</div>
          {convertedSub && (
            <div className="text-[9px] md:text-[10px] text-foreground-400 truncate">{convertedSub}</div>
          )}
          <div className="flex items-center justify-between gap-1.5 mt-1.5">
            <span className="hidden md:flex text-[11px] text-foreground-400 items-center gap-1 min-w-0 truncate">
              <i className="ri-map-pin-line"></i>
              {annonce.ville || 'Ville non précisée'}
            </span>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {reserveHref && (
                <Link
                  to={reserveHref}
                  className="pointer-events-auto flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] md:text-[11px] font-semibold bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors no-underline whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line"></i>
                  Réserver
                </Link>
              )}
              {annonce.telephone && (
                <a
                  href={`tel:${annonce.telephone}`}
                  className="pointer-events-auto flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] md:text-[11px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
                >
                  <i className="ri-phone-line"></i>
                  <span className="hidden md:inline">Contacter</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}